/**
 * Zustand store — current room / game state.
 */

import { create } from 'zustand';
import type { Player } from '@playground/game-sdk';

export type RoomStatus = 'idle' | 'waiting' | 'playing' | 'finished';

export interface RoomStore {
  roomId: string | null;
  gameId: string | null;
  players: Player[];
  gameState: unknown;
  status: RoomStatus;
  seq: number;
  /** Player IDs who won the last game (empty until game_over received). */
  winners: string[];
  /** Scores from the last game. */
  scores: Record<string, number>;
  /** Reason the game ended. */
  gameOverReason: string;
  /** Set by non-host players when host starts a rematch. */
  rematchRoomId: string | null;
  /** Server-client clock offset in ms (positive = server ahead). Computed from msg.ts on each state update. */
  serverClockOffset: number;

  setRoom(params: {
    roomId: string;
    gameId: string;
    players: Player[];
    status: RoomStatus;
  }): void;

  updateState(params: {
    gameState: unknown;
    players?: Player[];
    status?: RoomStatus;
    seq: number;
    serverClockOffset?: number;
  }): void;

  upsertPlayer(player: Player): void;
  removePlayer(playerId: string): void;

  finishRoom(params: {
    winners: string[];
    scores: Record<string, number>;
    reason: string;
  }): void;

  setRematchRoomId(id: string): void;

  reset(): void;
}

export const useRoomStore = create<RoomStore>()((set) => ({
  roomId: null,
  gameId: null,
  players: [],
  gameState: null,
  status: 'idle',
  seq: -1,
  winners: [],
  scores: {},
  gameOverReason: '',
  rematchRoomId: null,
  serverClockOffset: 0,

  setRoom({ roomId, gameId, players, status }) {
    set({
      roomId, gameId, players, status,
      gameState: null, seq: -1,
      winners: [], scores: {}, gameOverReason: '', rematchRoomId: null,
      serverClockOffset: 0,
    });
  },

  updateState({ gameState, players, status, seq, serverClockOffset }) {
    set((prev) => ({
      gameState,
      seq,
      players: players ?? prev.players,
      status: status ?? prev.status,
      serverClockOffset: serverClockOffset ?? prev.serverClockOffset,
    }));
  },

  upsertPlayer(player) {
    set((prev) => {
      const exists = prev.players.some((p) => p.id === player.id);
      if (exists) {
        return { players: prev.players.map((p) => (p.id === player.id ? player : p)) };
      }
      return { players: [...prev.players, player] };
    });
  },

  removePlayer(playerId) {
    set((prev) => ({
      players: prev.players.filter((p) => p.id !== playerId),
    }));
  },

  finishRoom({ winners, scores, reason }) {
    set({ status: 'finished', winners, scores, gameOverReason: reason });
  },

  setRematchRoomId(id) {
    set({ rematchRoomId: id });
  },

  reset() {
    set({
      roomId: null, gameId: null, players: [], gameState: null,
      status: 'idle', seq: -1,
      winners: [], scores: {}, gameOverReason: '', rematchRoomId: null,
      serverClockOffset: 0,
    });
  },
}));
